import React from 'react';
import { Briefcase, Clock, FileText, AlertCircle, Inbox, Users, TrendingUp, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface KpiCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  description?: string;
  variant?: 'default' | 'warning' | 'success' | 'info';
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, description, variant = 'default' }) => {
  const variantStyles = {
    default: 'bg-[hsl(215,30%,18%)] border-[hsl(215,25%,30%)]/50',
    warning: 'bg-[hsl(215,30%,18%)] border-[hsl(215,25%,30%)]/50',
    success: 'bg-[hsl(215,30%,18%)] border-[hsl(215,25%,30%)]/50',
    info: 'bg-[hsl(215,30%,18%)] border-[hsl(215,25%,30%)]/50',
  };

  const iconStyles = {
    default: 'text-amber-400',
    warning: 'text-amber-400',
    success: 'text-emerald-400',
    info: 'text-blue-400',
  };

  return (
    <div className={`rounded-xl p-5 border ${variantStyles[variant]} transition-all hover:scale-[1.02]`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">{label}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-gray-900/50 ${iconStyles[variant]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const translations = {
  en: {
    todo: {
      assigned: "Assigned Files",
      assignedDesc: "Total active workload",
      today: "To Process Today",
      todayDesc: "High Priority",
      cim: "CIM to Finalize",
      cimDesc: "Ready for review",
      waiting: "Awaiting Client",
      waitingDesc: "Client action required",
    },
    triage: {
      toSort: "Files to Sort",
      toSortDesc: "Unassigned",
      active: "Total Active Files",
      activeDesc: "All ongoing files",
      highPriority: "High Priority",
      highPriorityDesc: "Need attention",
    },
    all: {
      ongoing: "Ongoing Files",
      ongoingDesc: "Not archived",
      preEval: "Pre-evaluation",
      preEvalDesc: "pending_review + review_in_progress",
      eval: "Evaluation / CIM",
      evalDesc: "evaluation + cim_review",
      waiting: "Awaiting Client",
      waitingDesc: "needs_more_info",
    },
    pipeline: {
      total: "Total Pipeline",
      totalDesc: "Active files",
      review: "In Review",
      reviewDesc: "Staff in action",
      cimReady: "CIM Ready",
      cimReadyDesc: "To validate",
      completed: "Completed",
      completedDesc: "This month",
    },
  },
  fr: {
    todo: {
      assigned: "Dossiers Assignés",
      assignedDesc: "Total de ma charge active",
      today: "À Traiter Aujourd'hui",
      todayDesc: "Priorité Haute",
      cim: "CIM à Finaliser",
      cimDesc: "Prêts pour vérification",
      waiting: "En Attente Client",
      waitingDesc: "Action requise côté client",
    },
    triage: {
      toSort: "Dossiers à Trier",
      toSortDesc: "Non assignés",
      active: "Dossiers Actifs Total",
      activeDesc: "Tous les dossiers en cours",
      highPriority: "Priorité Haute",
      highPriorityDesc: "Nécessitent attention",
    },
    all: {
      ongoing: "Dossiers en Cours",
      ongoingDesc: "Non archivés",
      preEval: "En Pré-évaluation",
      preEvalDesc: "pending_review + review_in_progress",
      eval: "En Évaluation / CIM",
      evalDesc: "evaluation + cim_review",
      waiting: "En Attente Client",
      waitingDesc: "needs_more_info",
    },
    pipeline: {
      total: "Total Pipeline",
      totalDesc: "Dossiers actifs",
      review: "En Révision",
      reviewDesc: "Staff en action",
      cimReady: "CIM Ready",
      cimReadyDesc: "À valider",
      completed: "Complétés",
      completedDesc: "Ce mois",
    },
  },
};

interface StaffKpiCardsProps {
  kpis: {
    assignedDeals: number;
    todayDeals: number;
    cimToReview: number;
    waitingClient: number;
    triageCount?: number;
    totalActive?: number;
    preEvaluation?: number;
    evaluation?: number;
  };
  view: 'todo' | 'triage' | 'all' | 'pipeline';
}

export const StaffKpiCards: React.FC<StaffKpiCardsProps> = ({ kpis, view }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;

  if (view === 'todo') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={t.todo.assigned} value={kpis.assignedDeals} icon={<Briefcase className="w-5 h-5" />} description={t.todo.assignedDesc} />
        <KpiCard label={t.todo.today} value={kpis.todayDeals} icon={<Clock className="w-5 h-5" />} description={t.todo.todayDesc} variant="warning" />
        <KpiCard label={t.todo.cim} value={kpis.cimToReview} icon={<FileText className="w-5 h-5" />} description={t.todo.cimDesc} variant="info" />
        <KpiCard label={t.todo.waiting} value={kpis.waitingClient} icon={<AlertCircle className="w-5 h-5" />} description={t.todo.waitingDesc} variant={kpis.waitingClient > 0 ? 'warning' : 'default'} />
      </div>
    );
  }

  if (view === 'triage') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard label={t.triage.toSort} value={kpis.triageCount || 0} icon={<Inbox className="w-5 h-5" />} description={t.triage.toSortDesc} variant="warning" />
        <KpiCard label={t.triage.active} value={kpis.totalActive || 0} icon={<Briefcase className="w-5 h-5" />} description={t.triage.activeDesc} />
        <KpiCard label={t.triage.highPriority} value={kpis.todayDeals} icon={<AlertCircle className="w-5 h-5" />} description={t.triage.highPriorityDesc} variant="warning" />
      </div>
    );
  }

  if (view === 'all') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={t.all.ongoing} value={kpis.totalActive || 0} icon={<Briefcase className="w-5 h-5" />} description={t.all.ongoingDesc} />
        <KpiCard label={t.all.preEval} value={kpis.preEvaluation || 0} icon={<Clock className="w-5 h-5" />} description={t.all.preEvalDesc} variant="info" />
        <KpiCard label={t.all.eval} value={kpis.evaluation || 0} icon={<FileText className="w-5 h-5" />} description={t.all.evalDesc} variant="success" />
        <KpiCard label={t.all.waiting} value={kpis.waitingClient} icon={<AlertCircle className="w-5 h-5" />} description={t.all.waitingDesc} variant="warning" />
      </div>
    );
  }

  // Pipeline view
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard label={t.pipeline.total} value={kpis.totalActive || 0} icon={<TrendingUp className="w-5 h-5" />} description={t.pipeline.totalDesc} />
      <KpiCard label={t.pipeline.review} value={kpis.preEvaluation || 0} icon={<Users className="w-5 h-5" />} description={t.pipeline.reviewDesc} variant="info" />
      <KpiCard label={t.pipeline.cimReady} value={kpis.cimToReview} icon={<FileText className="w-5 h-5" />} description={t.pipeline.cimReadyDesc} variant="success" />
      <KpiCard label={t.pipeline.completed} value={0} icon={<CheckCircle className="w-5 h-5" />} description={t.pipeline.completedDesc} variant="success" />
    </div>
  );
};
